export type AppointmentStatus =
    | 'REQUESTED' | 'COUNTER_PROPOSED' | 'CHANGE_REQUESTED' | 'CONFIRMED'
    | 'COMPLETED' | 'REFUSED' | 'CANCELLED' | 'EXPIRED' | 'NO_SHOW';

export interface HistoryEntry { at: string; event: string; actor?: 'GARAGE' | 'CLIENT' | 'SYSTEM'; text?: string }
export interface QuoteSnapshot { quoteNumber?: string; totalTTC?: number; label?: string; items?: Array<{ description: string; totalTTC: number }> }

export interface Proposal {
    by: 'GARAGE' | 'CLIENT';
    date: string;
    timeSlot: string;
    duration?: number;
    note?: string;
    at?: string;
    outcome: 'PENDING' | 'ACCEPTED' | 'REFUSED' | 'SUPERSEDED' | 'EXPIRED';
}

export interface Appointment {
    _id: string;
    garageId: string;
    clientId?: string;
    inspectionId?: string;
    quoteId?: string;
    clientName: string;
    clientPhone?: string;
    vehicleBrand?: string;
    vehicleModel?: string;
    vehiclePlateNumber?: string;
    type: string;
    serviceMode?: 'DROP_OFF' | 'WAIT';
    date: string;        // minuit UTC du jour local du garage
    timeSlot: string;    // HH:mm
    duration: number;
    endTime?: string;
    status: AppointmentStatus;
    awaiting?: 'GARAGE' | 'CLIENT' | null;
    pendingProposal?: Proposal | null;
    proposals?: Proposal[];
    counterCount?: number;
    expiresAt?: string;
    clientNote?: string;
    description?: string;
    garageNotes?: string;
    refusal?: { by: string; reason?: string };
    cancelReason?: string;
    createdAt?: string;
    history?: HistoryEntry[];
    quoteSnapshot?: QuoteSnapshot;
    remindClient?: boolean;
    bufferAfter?: number;
    startAt?: string;
}

export const ACTIVE_STATUSES: AppointmentStatus[] = ['REQUESTED', 'COUNTER_PROPOSED', 'CHANGE_REQUESTED', 'CONFIRMED'];
export const MAX_COUNTER = 3;

// Date calendaire locale du garage d'un RDV : "YYYY-MM-DD" (jamais de conversion de fuseau)
export const dayKey = (iso: string): string => iso.slice(0, 10);

export const todayKey = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const addDays = (key: string, n: number): string => {
    const d = new Date(`${key}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

// Lundi de la semaine contenant `key`
export const startOfWeek = (key: string): string => {
    const dow = new Date(`${key}T00:00:00Z`).getUTCDay(); // 0 = dimanche
    return addDays(key, dow === 0 ? -6 : 1 - dow);
};

export const timeToMin = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
};

export const minToTime = (m: number): string =>
    `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

export const endTimeOf = (a: { timeSlot: string; duration: number }): string => minToTime(timeToMin(a.timeSlot) + a.duration);

const LOCALES: Record<string, string> = { fr: 'fr-FR', en: 'en-GB', ar: 'ar' };

export const formatDay = (key: string, lang: string, opts: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' }): string =>
    new Intl.DateTimeFormat(LOCALES[lang] || 'fr-FR', { ...opts, timeZone: 'UTC' }).format(new Date(`${key}T00:00:00Z`));

// Le créneau "sur la table" : la proposition en cours, sinon le créneau courant
export const currentSlot = (a: Appointment): { date: string; timeSlot: string; duration: number } =>
    a.pendingProposal && ['COUNTER_PROPOSED', 'CHANGE_REQUESTED'].includes(a.status)
        ? { date: dayKey(a.pendingProposal.date), timeSlot: a.pendingProposal.timeSlot, duration: a.pendingProposal.duration || a.duration }
        : { date: dayKey(a.date), timeSlot: a.timeSlot, duration: a.duration };

export const needsGarageAnswer = (a: Appointment): boolean =>
    a.awaiting === 'GARAGE' && ['REQUESTED', 'COUNTER_PROPOSED', 'CHANGE_REQUESTED'].includes(a.status);

// Un statut inconnu (anciennes données) retombe sur un style neutre
const NEUTRAL = { badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
export const statusStyle: Record<AppointmentStatus, { badge: string; dot: string }> = {
    REQUESTED: { badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-400' },
    COUNTER_PROPOSED: { badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-400' },
    CHANGE_REQUESTED: { badge: 'bg-orange-100 text-orange-800', dot: 'bg-orange-400' },
    CONFIRMED: { badge: 'bg-indigo-100 text-indigo-800', dot: 'bg-indigo-500' },
    COMPLETED: { badge: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
    REFUSED: { badge: 'bg-red-100 text-red-700', dot: 'bg-red-400' },
    CANCELLED: { badge: 'bg-red-100 text-red-700', dot: 'bg-red-400' },
    EXPIRED: { badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
    NO_SHOW: { badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' }
};

export const styleOf = (status: string) => (statusStyle as Record<string, { badge: string; dot: string }>)[status] || NEUTRAL;

// Message d'erreur localisé à partir du code renvoyé par l'API
export const errorCode = (err: any): string => err?.response?.data?.code || 'generic';

export const vehicleLabel = (a: Pick<Appointment, 'vehicleBrand' | 'vehicleModel'>): string =>
    [a.vehicleBrand, a.vehicleModel].filter(Boolean).join(' ');

// Intitulé de l'intervention : première ligne du devis accepté, sinon le type de RDV
export const interventionLabel = (a: Appointment, typeLabel: string): string => a.quoteSnapshot?.label || typeLabel;

export const formatMoney = (n: number | undefined, lang: string, currency = 'EUR'): string =>
    n == null ? '—' : new Intl.NumberFormat(LOCALES[lang] || 'fr-FR', { style: 'currency', currency }).format(n);

// État d'un RDV dans le widget du jour : Terminé / En cours / Prochain / À venir
export type DayState = 'done' | 'ongoing' | 'next' | 'upcoming';
export const dayStates = (list: Appointment[]): Record<string, DayState> => {
    const out: Record<string, DayState> = {};
    const sorted = [...list].sort((a, b) => a.timeSlot.localeCompare(b.timeSlot));
    let nextAssigned = false;
    const now = Date.now();
    sorted.forEach(a => {
        const started = a.startAt ? new Date(a.startAt).getTime() <= now : false;
        // L'avancement de la réparation est celui de l'inspection : ici « en cours » = le créneau a commencé
        if (a.status === 'COMPLETED' || a.status === 'NO_SHOW') out[a._id] = 'done';
        else if (started) out[a._id] = 'ongoing';
        else if (!nextAssigned) { out[a._id] = 'next'; nextAssigned = true; }
        else out[a._id] = 'upcoming';
    });
    return out;
};

// Minutes avant le début (négatif si passé), d'après l'heure locale du navigateur du garage
export const minutesUntil = (a: Appointment): number | null => {
    if (!a.startAt) return null;
    return Math.round((new Date(a.startAt).getTime() - Date.now()) / 60000);
};
